import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ApiService } from 'app/services/api.service';
import { BehaviorSubject } from 'rxjs';
import { INightowlCustomerId } from 'app/models/device.model';

export interface INightowlForm {
  nightowlId: string;
  lyraId: string;
  locationId: string;
  deviceName: string;
  customerId: string | null;
}

@Component({
    selector: 'app-nightowl-form',
    imports: [CommonModule, TranslateModule, ReactiveFormsModule, MatTooltipModule, MatFormFieldModule, MatSelectModule, MatOptionModule],
    templateUrl: './nightowl-form.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['./nightowl-form.component.css']
})
export class NightowlFormComponent implements AfterViewInit {
  @Output() savedEvent = new EventEmitter<string>();

  @Input() locations: { id: string, name: string }[];
  @Input() errorMsg$: BehaviorSubject<string>;

  form: FormGroup;
  showSpinner: boolean = false;
  customerIds: INightowlCustomerId[] = [];

  get nightowlId() {
    return this.form.get('nightowlId')?.value;
  }

  set nightowlId(value: any) {
    this.form.get('nightowlId')?.setValue(value);
  }

  constructor(private api: ApiService) {
    this.form = new FormGroup({
      nightowlId: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.pattern(/^NUA.{5}$/)]), // Pattern for "NUAxxxxx"
      lyraId: new FormControl({ value: '', disabled: true }, Validators.required),
      customerId: new FormControl(''),
      locationId: new FormControl('', Validators.required),
      deviceName: new FormControl(''),
    });
  }

  verifyId(): void {
    const nightowlId = this.nightowlId;
    this.showSpinner = true;
    this.errorMsg$?.next("");

    this.api.getNightowlAvailability(nightowlId).subscribe({
      next: res => {
        // Reassign the value in case the user has changed it while the request was in progress
        this.nightowlId = nightowlId;
        this.showSpinner = false;

        if (!res) {
          this.errorMsg$?.next('ERROR');
          console.error('API error: no response received');
        }
        else if (!res.available) {
          this.errorMsg$?.next('DEVICE_VALIDITY_INUSE');
        }
        else if (!res.lyraId) {
          this.errorMsg$?.next('ERROR');
          console.error('API error: Lyra ID was not provided');
        }
        else {
          this.form.controls.lyraId.setValue(res.lyraId);
          this.form.controls.nightowlId.disable();

          this.getCompanyExternalSystemId();
        }
      },
      error: err => {
        console.error(err);
        this.errorMsg$?.next("ERROR");
        this.nightowlId = nightowlId;
        this.showSpinner = false;
      }
    })
  }

  getCompanyExternalSystemId(): void {
    this.api.getNightowlCustomerId().subscribe((response) => {
      let customerIds: INightowlCustomerId[] = [];

      // Handle both formats: string or array of objects
      if (typeof response === 'string') {
        // Add single element to customerIds array 
        customerIds = [{ id: response, name: "" }];
      }
      else if (Array.isArray(response)) {
        // New format: array of objects with id and name
        customerIds = response;
      }
      this.customerIds = customerIds;

      // If multiple customer IDs exist, make the field required
      if (this.hasMultipleCustomerIds()) {
        this.form.controls.customerId.setValidators(Validators.required);
        this.form.controls.customerId.updateValueAndValidity();
      }
    });
  }

  hasMultipleCustomerIds(): boolean {
    return this.customerIds.length > 1;
  }


  submit(): INightowlForm {
    if (this.form.invalid) return null;

    const selectedCustomerId = this.form.controls.customerId.value ? this.form.controls.customerId.value : this.customerIds.length === 1 ? this.customerIds[0].id : null;
    if (selectedCustomerId === null) {
      this.errorMsg$?.next("NIGHTOWL_CUSTOMER_ID_NOT_SET");
      return null;
    }

    const request: INightowlForm = {
      nightowlId: this.form.controls.nightowlId.value,
      lyraId: this.form.controls.lyraId.value,
      locationId: this.form.controls.locationId.value,
      deviceName: this.form.controls.deviceName.value,
      customerId: selectedCustomerId
    }

    this.showSpinner = true;

    this.api.saveNightowl(request).subscribe({
      next: () => {
        this.showSpinner = false;
        this.savedEvent.emit(request.lyraId);

        if (localStorage.getItem("features") != null && JSON.parse(localStorage.getItem("features")).includes("DeviceRegistry")) {
          const dev = { Id: request.lyraId, DevClass: 4, Name: request.deviceName };
          this.api.addDeviceToInventory(dev).subscribe(() => {
          }, (err) => {
            console.error(err);
          });
        }
      },
      error: err => {
        console.error(err);
        this.errorMsg$?.next("ERROR");
        this.showSpinner = false;
      }
    });

    return request;
  }

  clearForm(): void {
    this.form.reset();
    this.form.controls.locationId.setValue('');
    this.form.controls.customerId.setValue('');
    this.form.controls.nightowlId.enable();
    this.customerIds = [];
  }

  ngAfterViewInit(): void {
    // Use a timeout to avoid NG0100: ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.errorMsg$?.next(""));
  }
}
